import { useState, useCallback, useEffect } from 'react'
import {
  Button,
  Icon,
  Flex,
  Text,
  VStack,
  useToast,
  Box,
  Input,
  HStack,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react'
import { ArrowLeftIcon, DragGridIcon, WarningIcon, ChevronUpIcon, ChevronDownIcon } from '../../../assets/icons'
import { Trash02Icon } from '../../../assets/icons'
import { AddIcon } from '@chakra-ui/icons'
import QuestionnaireSidebar from '../../../components/QuestionnaireSidebar'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import MasterCard from '../../../components/contentcards/base/MasterCard'
import SideBar from '../../../components/contentcards/base/SideBar'
import { useNavigate, useParams } from 'react-router-dom'
import { ContentType } from '../../../types/content/content'
import handleToast from '../../../components/HandleToast'
import { ContentItemType } from '../../../types/content/contentItems'
import { useLocation } from 'react-router-dom'
import { contentItems } from '../../../hooks/content/contentItems'
import { fetchPages } from '../../../api/content/contentPage'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import NavigationTab from '../../../components/NavigationTab'
import { fetchContent, publishContent } from '../../../api/content/content'
import { addContentResponse } from '../../../api/content/contentResponse'
import { content } from '../../../hooks/content/content'
import { useRole } from '../../../hooks/auth/useRole'
import { CONTENT_EXPERIENCE } from './contentExperienceStyles'

// ── Visual constants matching QuestionnaireLayout ──────────────────────────
const SHADOW_DEFAULT = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)'
const SHADOW_ACTIVE  = '0 0 0 2px rgba(35, 39, 47, 0.18)'
const CARD_PALETTES  = [
  { bg: 'background.White', accent: 'solid.600' },
]

const Divider = () => <Box w="1px" h="16px" bg="solid.100" flexShrink={0} />

const isQuizPageName = (name: any) =>
  String(name ?? '').trim().toLowerCase() === 'quiz'

// ──────────────────────────────────────────────────────────────────────────
interface QuestionnaireProps {
  modalId?: string | null
  isModal?: boolean
  onComplete?: () => void
  onClose?: () => void
}

export default function Questionnaire({
  modalId = null,
  isModal = false,
  onComplete = () => {},
  onClose,
}: QuestionnaireProps) {
  const { id } = useParams<{ id: string }>()
  const { patchContentMutation } = content()
  const effectiveId = isModal ? modalId : id
  const location = useLocation()

  const locationState = location.state as {
    contentId?: string
    contentType?: ContentType
    is_draft?: boolean
    minimum_score?: number
    duration_minutes?: number
  } | null

  let contentId = effectiveId || locationState?.contentId
  if (!contentId) {
    const match = window.location.pathname.match(/\/user\/content\/([^/]+)/)
    if (match) contentId = match[1]
  }

  const contentQuery = useQuery({
    queryKey: ['content', contentId],
    queryFn: () =>
      contentId ? fetchContent(contentId) : Promise.reject('No content ID provided'),
    enabled: !!contentId,
  })

  const contentType: ContentType =
    locationState?.contentType ||
    (contentQuery.data?.content_type as ContentType) ||
    ContentType.Skills

  const isSurveyLike =
    contentType === ContentType.Survey || contentType === ContentType.Learning

  const isDraft = locationState?.is_draft ?? contentQuery.data?.is_draft

  const {
    useGetContentItemsListQuery,
    createContentItemMutation,
    deleteContentItemMutation,
    patchContentItemMutation,
  } = contentItems()
  const queryClient = useQueryClient()

  const contentItemsQuery = contentId
    ? useGetContentItemsListQuery({ id: contentId })
    : { data: null, isLoading: false }

  const [oldQuestions, setOldQuestions]     = useState<any[]>([])
  const [newQuestions, setNewQuestions]     = useState<any[]>([])
  const [questionErrors, setQuestionErrors] = useState<Record<number, string>>({})

  useEffect(() => {
    if (contentItemsQuery.data) {
      let loadedQuestions: any[] = []
      if (Array.isArray(contentItemsQuery.data)) {
        loadedQuestions = contentItemsQuery.data
      } else if (contentItemsQuery.data.data && Array.isArray(contentItemsQuery.data.data)) {
        loadedQuestions = contentItemsQuery.data.data
      }
      setOldQuestions(loadedQuestions.map((q) => ({ ...q })))
      setNewQuestions(loadedQuestions.map((q) => ({ ...q })))
    }
  }, [contentItemsQuery.data])

  const navigate = useNavigate()

  const updateIndices = (questions: any[]) =>
    questions.map((q, idx) => ({ ...q, index: idx }))

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return
    setNewQuestions((prev) => {
      const newQs = [...prev]
      const [removed] = newQs.splice(source.index, 1)
      newQs.splice(destination.index, 0, removed)
      removed.index = destination.index
      return updateIndices(newQs)
    })
  }

  const moveQuestion = useCallback((dragIndex: number, hoverIndex: number) => {
    setNewQuestions((prev) => {
      const newQs = [...prev]
      const [removed] = newQs.splice(dragIndex, 1)
      newQs.splice(hoverIndex, 0, removed)
      removed.index = hoverIndex
      return updateIndices(newQs)
    })
  }, [])

  const handleAddQuestion = (qType: ContentItemType) => {
    setNewQuestions((prev) => {
      const pageId = pages[currentPage - 1]?.id
      const newQ: any = {
        id: Date.now(),
        type: qType,
        index: prev.length,
        ...(pageId ? { page: pageId } : {}),
      }
      if (qType === ContentItemType.LearningCheckbox) {
        newQ.label = ''
        newQ.title = ''
      }
      return updateIndices([...prev, newQ])
    })
  }

  const handleAddDefaultQuestionForPage = (pageId: number) => {
    setNewQuestions((prev) => {
      const newQ: any = {
        id: Date.now(),
        type: ContentItemType.Paragraph,
        index: prev.length,
        title: '',
        paragraph: '',
        page: pageId,
      }
      return updateIndices([...prev, newQ])
    })
  }

  const handleDeleteQuestion = (id: number) => {
    setNewQuestions((prev) => updateIndices(prev.filter((q) => q.id !== id)))
    setQuestionErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleDeleteQuestionsForPage = (pageId: number) => {
    setNewQuestions((prev) => updateIndices(prev.filter((q) => q.page !== pageId)))
  }

  const toast = useToast()
  const { isUserMode } = useRole()

  const [isActionInProgress, setIsActionInProgress] = useState(false)
  const [quizMode, setQuizMode]       = useState(isModal || isUserMode)
  const [quizSubmitted, setQuizSubmitted] = useState(false)

  const [pages, setPages]             = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [editingPageId, setEditingPageId] = useState<number | null>(null)
  const [pageNames, setPageNames]     = useState<Record<number, string>>({})

  const [mcqSelections, setMcqSelections]                     = useState<Record<string, number | null>>({})
  const [checkboxSelections, setCheckboxSelections]           = useState<Record<string, number[]>>({})
  const [surveyMCQSelections, setSurveyMCQSelections]         = useState<Record<string, number | null>>({})
  const [surveyRatingSelections, setSurveyRatingSelections]   = useState<Record<string, number | null>>({})
  const [surveyTextAnswer, setSurveyTextAnswer]               = useState<Record<string, string | null>>({})

  const [minimumScore, setMinimumScore] = useState<number>(() => {
    const val = Number(locationState?.minimum_score ?? 0)
    return Number.isNaN(val) ? 0 : Math.min(100, Math.max(0, val))
  })
  const [durationMinutes, setDurationMinutes] = useState<number>(() => {
    const val = Number(locationState?.duration_minutes ?? 0)
    return Number.isNaN(val) ? 0 : Math.max(0, val)
  })
  const [pageDurations, setPageDurations] = useState<Record<number, number>>({})

  // Active-card highlight state (same as QuestionnaireLayout)
  const [activeCardId, setActiveCardId] = useState<number | null>(null)
  const [titleFocused, setTitleFocused] = useState(false)
  const userMode = isUserMode

  const increment = () => setMinimumScore((prev) => Math.min(100, prev + 5))
  const decrement = () => setMinimumScore((prev) => Math.max(0, prev - 5))

  useEffect(() => {
    if (userMode && !quizSubmitted) {
      setQuizMode(true)
    }
  }, [quizSubmitted, userMode])

  useEffect(() => {
    const loadPages = async () => {
      try {
        if (!contentId) return
        const res = await fetchPages(String(contentId))
        const pagesData: any[] = Array.isArray(res)
          ? res
          : Array.isArray((res as any)?.data)
          ? (res as any).data
          : []
        setPages(pagesData)
        const firstNonTestIdx = pagesData.findIndex((p: any) => !isQuizPageName(p?.name))
        setCurrentPage(firstNonTestIdx >= 0 ? firstNonTestIdx + 1 : 1)
        if (pagesData.length > 0) {
          const durations: Record<number, number> = {}
          pagesData.forEach((p: any, idx: number) => { durations[idx + 1] = p.duration || 0 })
          setPageDurations((prev) => (Object.keys(prev).length ? prev : durations))
        }
      } catch (e) {
        console.error('Failed to fetch pages', e)
      }
    }
    loadPages()
  }, [contentId])

  const getCurrentPageId = () =>
    pages && pages.length > 0 ? pages[currentPage - 1]?.id : undefined

  const getCurrentPageQuestions = () => {
    const pid = getCurrentPageId()
    if (!pid) return newQuestions
    return newQuestions.filter((q) => q.page === pid)
  }

  const getTotalPages = () => Math.max(1, pages.length)

  useEffect(() => {
    const totalPages = getTotalPages()
    if (currentPage > totalPages) {
      const firstNonTestIdx = pages.findIndex((p: any) => !isQuizPageName(p?.name))
      setCurrentPage(firstNonTestIdx >= 0 ? firstNonTestIdx + 1 : 1)
    }
  }, [pages.length, currentPage])

  // ── Selection handlers ─────────────────────────────────────────────────
  const handleMCQSelect = (questionId: string, selectedIndex: number, _isCorrect: boolean) => {
    setMcqSelections((prev) => ({ ...prev, [questionId]: selectedIndex }))
    setNewQuestions((prev) =>
      prev.map((q) =>
        String(q.id) === String(questionId) ? { ...q, _userSelection: selectedIndex } : q,
      ),
    )
  }

  const handleSurveyMCQSelect = (questionId: string, selectedIndex: number) => {
    setSurveyMCQSelections((prev) => ({ ...prev, [questionId]: selectedIndex }))
    setNewQuestions((prev) =>
      prev.map((q) =>
        String(q.id) === String(questionId) ? { ...q, _userSelection: selectedIndex } : q,
      ),
    )
  }

  const handleCheckboxToggle = (questionId: string, optionIndex: number, _isCorrect: boolean) => {
    setCheckboxSelections((prev) => {
      const current = prev[questionId] || []
      const updated = current.includes(optionIndex)
        ? current.filter((idx) => idx !== optionIndex)
        : [...current, optionIndex]
      return { ...prev, [questionId]: updated }
    })
    setNewQuestions((prev) => {
      const currentSel = (checkboxSelections[questionId] || []).slice()
      const newSel = currentSel.includes(optionIndex)
        ? currentSel.filter((idx) => idx !== optionIndex)
        : [...currentSel, optionIndex]
      return prev.map((q) =>
        String(q.id) === String(questionId) ? { ...q, _userCheckboxSelection: newSel } : q,
      )
    })
  }

  const handleSurveyRatingSelect = (questionId: string, rating: number) => {
    setSurveyRatingSelections((prev) => ({ ...prev, [questionId]: rating }))
    setNewQuestions((prev) =>
      prev.map((q) =>
        String(q.id) === String(questionId) ? { ...q, _userRating: rating } : q,
      ),
    )
  }

  const handleSurveyTextAnswer = (questionId: string, answer: string) => {
    setSurveyTextAnswer((prev) => ({ ...prev, [questionId]: answer }))
    setNewQuestions((prev) =>
      prev.map((q) =>
        String(q.id) === String(questionId) ? { ...q, _userTextAnswer: answer } : q,
      ),
    )
  }

  // ── Quiz submit / exit ─────────────────────────────────────────────────
  const handleQuizSubmit = async () => {
    try {
      const trainingAnswers = newQuestions
        .filter(
          (q) =>
            (q.type === 'training_multiple_choice' && q.id.toString() in mcqSelections) ||
            (q.type === 'training_checkbox' && q.id.toString() in checkboxSelections),
        )
        .map((q) => {
          const qIdString = q.id.toString()
          let answer
          if (q.type === 'training_multiple_choice') {
            const selectedIndex = mcqSelections[qIdString]
            answer =
              q.answers && selectedIndex !== undefined && selectedIndex !== null
                ? q.answers[selectedIndex]?.text || ''
                : ''
          } else if (q.type === 'training_checkbox') {
            const selectedIndices = checkboxSelections[qIdString] || []
            answer =
              q.answers && selectedIndices.length > 0
                ? selectedIndices.map((idx) => q.answers[idx]?.text).filter(Boolean)
                : []
          }
          return {
            type: q.type,
            question: qIdString,
            answer: answer || (q.type === 'training_checkbox' ? [] : ''),
          }
        })

      const surveyAnswers = newQuestions
        .filter(
          (q) =>
            (q.type === 'survey_multiple_choice' && q.id.toString() in surveyMCQSelections) ||
            (q.type === 'survey_rating' && q.id.toString() in surveyRatingSelections) ||
            (q.type === 'survey_text' && q.id.toString() in surveyTextAnswer),
        )
        .map((q) => {
          const qIdString = q.id.toString()
          let answer
          if (q.type === 'survey_multiple_choice') {
            const selectedIndex = surveyMCQSelections[qIdString]
            answer =
              q.answers && selectedIndex !== undefined && selectedIndex !== null
                ? q.answers[selectedIndex] || ''
                : 'No selection'
          } else if (q.type === 'survey_rating') {
            answer = surveyRatingSelections[qIdString] || null
          } else if (q.type === 'survey_text') {
            answer = surveyTextAnswer[qIdString] || null
          }
          return { type: q.type, question: qIdString, answer: answer || 'No response' }
        })

      if (!contentId) throw new Error('No content ID')
      await addContentResponse(contentId, { answers: [...trainingAnswers, ...surveyAnswers] })
      setQuizSubmitted(true)
      handleToast(toast, true, 'Responses submitted successfully', '')
      await queryClient.invalidateQueries({ queryKey: ['contentItemsList', String(contentId)] })
      if (onComplete) onComplete()
    } catch (error) {
      console.error('Error submitting responses:', error)
      handleToast(
        toast,
        false,
        'Failed to submit responses',
        error instanceof Error ? error.message : 'Unknown error',
      )
    }
  }

  const handleExitQuiz = () => {
    setQuizMode(false)
    setQuizSubmitted(false)
    setMcqSelections({})
    setCheckboxSelections({})
    setSurveyMCQSelections({})
    setSurveyRatingSelections({})
    setSurveyTextAnswer({})
    const firstNonTestIdx = pages.findIndex((pg: any) => !isQuizPageName(pg?.name))
    setCurrentPage(firstNonTestIdx >= 0 ? firstNonTestIdx + 1 : 1)
  }

  // ── Page ordering helpers ──────────────────────────────────────────────
  const getLogicalOrder = () => {
    const all = Array.isArray(pages) ? pages : []
    const nonQuiz = all
      .map((p: any, i: number) => ({ i, p }))
      .filter(({ p }) => !isQuizPageName(p?.name))
      .map(({ i }) => i)
    const quiz = all
      .map((p: any, i: number) => ({ i, p }))
      .filter(({ p }) => isQuizPageName(p?.name))
      .map(({ i }) => i)
    return [...nonQuiz, ...quiz]
  }

  const getLogicalPosition = () => {
    const order = getLogicalOrder()
    return order.indexOf(currentPage - 1)
  }
  const isLastLogicalPage = () => {
    const order = getLogicalOrder()
    if (order.length === 0) return true
    return getLogicalPosition() === order.length - 1
  }
  const goToLogicalOffset = (offset: number) => {
    const order = getLogicalOrder()
    if (order.length === 0) return
    const pos   = getLogicalPosition()
    const nextPos = pos + offset
    if (nextPos < 0 || nextPos >= order.length) return
    setCurrentPage(order[nextPos] + 1)
  }
  const handleNextPage = () => goToLogicalOffset(1)
  const handlePrevPage = () => goToLogicalOffset(-1)

  // ── Save / publish ─────────────────────────────────────────────────────
  const handleUpdateContent = async (draft: boolean) => {
    if (!contentId) return
    try {
      setIsActionInProgress(true)

      const pagesWithContent = new Set<number>()
      if (pages && pages.length) {
        const idToNumber = new Map<number, number>()
        pages.forEach((p: any, idx: number) => idToNumber.set(p.id, idx + 1))
        newQuestions.forEach((q) => pagesWithContent.add(idToNumber.get(q.page) || 1))
      } else {
        pagesWithContent.add(1)
      }

      const cleanedPageDurations: Record<number, number> = {}
      Object.entries(pageDurations).forEach(([key, value]) => {
        const pageNum = Number(key)
        if (pagesWithContent.has(pageNum)) cleanedPageDurations[pageNum] = value
      })
      setPageDurations(cleanedPageDurations)
      const totalDuration = Object.values(cleanedPageDurations).reduce(
        (sum, val) => sum + (Number(val) || 0),
        0,
      )

      const formattedPageDurations: Record<string, number> = {}
      Object.entries(cleanedPageDurations).forEach(([key, value]) => {
        formattedPageDurations[String(key)] = Number(value) || 0
      })

      await patchContentMutation.mutateAsync({
        contentId,
        contentData: {
          minimum_score: minimumScore,
          duration_minutes: totalDuration,
          page_durations: formattedPageDurations,
        },
      })
      setDurationMinutes(totalDuration)

      if (draft) {
        const invalidQuestions = newQuestions.filter((q) => {
          if (q.type === ContentItemType.TrainingMCQ || q.type === ContentItemType.TrainingCheckbox) {
            return (
              !q.answers ||
              !q.answers.some((a: any) => a.correct) ||
              !q.question ||
              !q.correct_feedback ||
              !q.incorrect_feedback
            )
          } else if (q.type === ContentItemType.SurveyMCQ) {
            const hasDuplicates =
              q.answers &&
              q.answers.some((a: any, idx: number) => q.answers.findIndex((b: any) => b === a) !== idx)
            return !q.answers || !q.question || hasDuplicates
          }
          return false
        })

        if (invalidQuestions.length > 0) {
          const invalidCards = invalidQuestions
            .map((q) => {
              const errors: string[] = []
              if (q.type === ContentItemType.TrainingMCQ || q.type === ContentItemType.TrainingCheckbox) {
                if (!q.answers) errors.push('No options added')
                else if (!q.answers.some((a: any) => a.correct)) errors.push('No correct answers selected')
                if (!q.question) errors.push('Missing Question text')
                if (!q.correct_feedback) errors.push('Missing correct feedback')
                if (!q.incorrect_feedback) errors.push('Missing incorrect feedback')
              } else if (q.type === ContentItemType.SurveyMCQ || q.type === ContentItemType.SurveyRating) {
                if (!q.answers) errors.push('No options added')
                else if (
                  q.answers.some(
                    (a: any, idx: number) => q.answers.findIndex((b: any) => b.text === a.text) !== idx,
                  )
                )
                  errors.push('Duplicate answers found')
                if (!q.question) errors.push('Missing Question text')
              }
              return `Card ${q.index + 1} (${q.type}): ${errors.join(', ')}`
            })
            .join('\n')
          handleToast(toast, false, `Cannot save draft`, `Some cards have errors:\n${invalidCards}`, 8000)
          setIsActionInProgress(false)
          return
        }
      }

      if (!draft) {
        const hasRequiredFields = (question: any) => {
          switch (question.type) {
            case ContentItemType.Paragraph: return question.title && question.paragraph
            case ContentItemType.Short:     return question.title && question.short
            case ContentItemType.Section:   return question.title
            case ContentItemType.SurveyMCQ:
            case ContentItemType.TrainingMCQ:
            case ContentItemType.TrainingCheckbox:
              return question.question && question.answers && question.answers.length > 0
            case ContentItemType.SurveyRating:
            case ContentItemType.SurveyText:
              return question.question
            case ContentItemType.Media: return question.media
            default: return true
          }
        }
        const invalidQ = newQuestions.filter((q) => !hasRequiredFields(q))
        if (invalidQ.length > 0) {
          const errors: Record<number, string> = {}
          invalidQ.forEach((q) => { errors[q.id] = 'This field is required.' })
          setQuestionErrors(errors)
          handleToast(toast, false, 'Please fill in all required fields before publishing.', '')
          setIsActionInProgress(false)
          return
        }
      }

      const existingQ = oldQuestions || []
      const toCreate = newQuestions.filter((q) => !existingQ.some((eq) => eq.id === q.id))
      const toUpdate = newQuestions.filter((q) => {
        const existing = existingQ.find((eq) => eq.id === q.id)
        return existing && JSON.stringify(buildPayload(q)) !== JSON.stringify(buildPayload(existing))
      })
      const toDelete = existingQ.filter((eq) => !newQuestions.some((q) => q.id === eq.id))

      const mutations: Promise<any>[] = []
      for (const q of toCreate)
        mutations.push(createContentItemMutation.mutateAsync({ id: contentId, contentItem: buildPayload(q) }))
      for (const q of toUpdate)
        mutations.push(
          patchContentItemMutation.mutateAsync({ id: contentId, itemId: q.id, contentItem: buildPayload(q, q.id) }),
        )
      for (const q of toDelete)
        mutations.push(deleteContentItemMutation.mutateAsync({ id: contentId, itemId: q.id }))

      await Promise.all(mutations)
      setOldQuestions([...newQuestions])
      await queryClient.invalidateQueries({ queryKey: ['contentItemsList', String(contentId)] })
      await queryClient.invalidateQueries({ queryKey: ['content', contentId] })
      handleToast(toast, true, 'Content updated successfully', '')
    } catch (error) {
      console.error('Error updating content:', error)
      handleToast(toast, false, 'Failed to update content', '')
    } finally {
      setIsActionInProgress(false)
    }
  }

  const buildPayload = (question: any, id?: string) => {
    const payload: any = {
      type: question.type,
      index: question.index,
      ...(question.page ? { page: question.page } : {}),
    }
    if (id) payload.id = id
    switch (question.type) {
      case ContentItemType.Paragraph:
        payload.title = question.title; payload.paragraph = question.paragraph; break
      case ContentItemType.Short:
        payload.title = question.title; payload.short = question.short; break
      case ContentItemType.Media:
        payload.media = question.media; break
      case ContentItemType.SurveyMCQ:
        payload.answers = question.answers; payload.question = question.question; break
      case ContentItemType.SurveyRating:
      case ContentItemType.SurveyText:
        payload.question = question.question; break
      case ContentItemType.LearningCheckbox:
        payload.label = question.label || question.text || question.title || ''; break
      case ContentItemType.Section:
        payload.title = question.title; break
      case ContentItemType.TrainingMCQ:
      case ContentItemType.TrainingCheckbox:
        payload.answers          = question.answers
        payload.question         = question.question
        payload.correct_feedback   = question.correct_feedback
        payload.incorrect_feedback = question.incorrect_feedback
        payload.points           = question.points
        break
      default:
        payload[question.type] = question[question.type]; break
    }
    return payload
  }

  const handleUpdate = (index: number, updatedFields: any) => {
    setNewQuestions((prev) => {
      const newQs = [...prev].map((q) => ({ ...q }))
      const question       = { ...newQs[index] }
      const updatedQuestion = { ...question }

      if (question.type === ContentItemType.SurveyMCQ) {
        updatedQuestion.answers  = updatedFields.answers || question.answers || []
        updatedQuestion.question = updatedFields.text    || question.text    || ''
      } else if (question.type === ContentItemType.SurveyRating || question.type === ContentItemType.SurveyText) {
        updatedQuestion.question = updatedFields.text || question.text || ''
      } else if (question.type === ContentItemType.Section) {
        updatedQuestion.title = updatedFields.text || question.text || ''
      } else if (question.type === ContentItemType.TrainingMCQ || question.type === ContentItemType.TrainingCheckbox) {
        updatedQuestion.answers            = updatedFields.answers ? [...updatedFields.answers] : question.answers ? [...question.answers] : []
        updatedQuestion.question           = updatedFields.text             || question.text             || ''
        updatedQuestion.correct_feedback   = updatedFields.correct_feedback   || question.correct_feedback   || ''
        updatedQuestion.incorrect_feedback = updatedFields.incorrect_feedback || question.incorrect_feedback || ''
        updatedQuestion.points             = updatedFields.points || question.points || 0
      } else if (question.type === ContentItemType.Paragraph) {
        updatedQuestion.title     = updatedFields.text      || question.text      || ''
        updatedQuestion.paragraph = updatedFields.paragraph || question.paragraph || ''
      } else if (question.type === ContentItemType.Short) {
        updatedQuestion.title = updatedFields.text  || question.text  || ''
        updatedQuestion.short = updatedFields.short || question.short || ''
      } else if (question.type === ContentItemType.LearningCheckbox) {
        const nextText = updatedFields.text || question.title || ''
        updatedQuestion.title = nextText
        updatedQuestion.label = nextText
      } else {
        updatedQuestion[question.type] = updatedFields.text || ''
      }

      newQs[index] = updatedQuestion

      if (
        updatedFields?.text !== undefined ||
        updatedFields?.file !== undefined ||
        updatedFields?.answers !== undefined
      ) {
        if (
          (typeof updatedFields.text === 'string' && updatedFields.text.trim() !== '') ||
          updatedFields.file ||
          (updatedFields.answers && updatedFields.answers.length > 0)
        ) {
          setQuestionErrors((prevErrors) => {
            const next = { ...prevErrors }
            delete next[newQs[index].id]
            return next
          })
        }
      }
      return newQs
    })
  }

  const unsavedChanges =
    JSON.stringify(newQuestions) !== JSON.stringify(contentItemsQuery.data?.data)

  const tabItems = [
    { title: 'Questions', tab: 'questions' },
    {
      title: 'Responses',
      tab: 'responses',
      path: `/user/content/${contentId}/questionnaire/responses/summary`,
    },
  ]
  const [selectedNavTab, setSelectedNavTab] = useState('questions')

  const handleTabChange = (tab: string) => {
    if (tab === 'responses') {
      navigate(`/user/content/${contentId}/questionnaire/responses/summary`, {
        state: { contentId, contentType, is_draft: isDraft },
      })
    } else {
      setSelectedNavTab(tab)
    }
  }

  const handleGoBack = () => {
    navigate(`/user/content/${contentId}`, {
      state: { contentId, contentType, is_draft: isDraft },
    })
  }

  const handlePublish = async () => {
    if (!contentId) return
    try {
      await publishContent(contentId.toString())
      handleToast(toast, true, 'Content published!', '')
    } catch (error) {
      console.error('Failed to publish content', error)
      handleToast(toast, false, 'Failed to publish content', '')
    }
  }

  useEffect(() => {
    if (contentQuery.data) {
      if (contentQuery.data.minimum_score !== undefined)
        setMinimumScore(contentQuery.data.minimum_score)
      if (contentQuery.data.duration_minutes !== undefined)
        setDurationMinutes(Number(contentQuery.data.duration_minutes) || 0)
      if (contentQuery.data.page_durations && typeof contentQuery.data.page_durations === 'object') {
        const loaded: Record<number, number> = {}
        Object.entries(contentQuery.data.page_durations).forEach(([key, value]) => {
          const pageNum  = Number(key)
          const duration = Number(value)
          if (!isNaN(pageNum) && !isNaN(duration)) loaded[pageNum] = duration
        })
        setPageDurations(loaded)
      }
    }
  }, [contentQuery.data])

  useEffect(() => {
    if (durationMinutes > 0 && Object.keys(pageDurations).length === 0)
      setPageDurations({ 1: durationMinutes })
  }, [durationMinutes])

  useEffect(() => {
    if (quizMode) {
      getCurrentPageQuestions().forEach((q) => {
        const questionId = String(q.id)
        if (q.type === 'training_multiple_choice' && q._userSelection !== undefined)
          setMcqSelections((prev) => ({ ...prev, [questionId]: q._userSelection }))
        if (q.type === 'survey_multiple_choice' && q._userSelection !== undefined)
          setSurveyMCQSelections((prev) => ({ ...prev, [questionId]: q._userSelection }))
        if (q.type === 'training_checkbox' && q._userCheckboxSelection !== undefined)
          setCheckboxSelections((prev) => ({ ...prev, [questionId]: q._userCheckboxSelection }))
        if (q.type === 'survey_rating' && q._userRating !== undefined)
          setSurveyRatingSelections((prev) => ({ ...prev, [questionId]: q._userRating }))
        if (q.type === 'survey_text' && q._userTextAnswer !== undefined)
          setSurveyTextAnswer((prev) => ({ ...prev, [questionId]: q._userTextAnswer }))
      })
    }
  }, [currentPage, quizMode, quizSubmitted])

  // ── Derived page data (mirrors QuestionnaireLayout) ────────────────────
  const nonQuizPages   = pages.filter((p: any) => !isQuizPageName(p?.name))
  const allPages       = Array.isArray(pages) ? pages : []
  const quizPageIdx    = allPages.findIndex((p: any) => isQuizPageName(p?.name))
  const quizDurationKey = quizPageIdx + 1
  const rawQuizDur =
    quizPageIdx >= 0
      ? (pageDurations[quizDurationKey] ?? (allPages[quizPageIdx] as any)?.duration)
      : undefined
  const quizDurationValue = !rawQuizDur || Number(rawQuizDur) <= 0 ? '' : rawQuizDur
  const currentPageObj  = pages[currentPage - 1]
  const logicalOrder = getLogicalOrder()
  const logicalPages = logicalOrder.map((pageIndex) => pages[pageIndex]).filter(Boolean)
  const logicalPosition = getLogicalPosition()
  const currentLogicalStep = Math.max(0, logicalPosition) + 1
  const isCurrentQuizPage = !!currentPageObj && isQuizPageName(currentPageObj?.name)
  const learningPageCount = nonQuizPages.length
  const currentPageQuestions = getCurrentPageQuestions()
  const currentLearningUrlItem =
    contentType === ContentType.Learning
      ? currentPageQuestions.find((q: any) => q.type === ContentItemType.URL && typeof q.url === 'string' && q.url.trim() !== '')
      : null
  const currentLearningConfirmation =
    contentType === ContentType.Learning
      ? currentPageQuestions.find((q: any) => q.type === ContentItemType.LearningCheckbox)
      : null
  const visiblePageTitle = currentPageObj?.name || `Page ${currentLogicalStep}`
  const visiblePageSubtitle =
    contentType === ContentType.Learning && !isCurrentQuizPage
      ? `Learning page ${Math.min(currentLogicalStep, Math.max(learningPageCount, 1))} of ${Math.max(learningPageCount, 1)}. Review the material here before continuing.`
      : 'Work through the items on this page, then continue when you are ready.'
  const contentTitle    =
    (contentQuery.data as any)?.title || (contentQuery.data as any)?.name || 'Update Content'
  const contentDescription =
    (contentQuery.data as any)?.description ||
    (contentType === ContentType.Training
      ? 'Guide people through the lesson and any required knowledge checks in a clear page-by-page flow.'
      : isSurveyLike
      ? 'Present the questionnaire as a focused response experience instead of an editor-first screen.'
      : 'Move through the content one page at a time.')

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════
  return (
    <Flex
      w="100%"
      h="100%"
      overflowY="auto"
      id="questionnaire-container"
      borderRadius="22px"
      bg={CONTENT_EXPERIENCE.pageBg}
    >
      <Flex flex="1" direction="column" height="auto">

        {/* ── Top header (non-modal) ─────────────────────────────────── */}
        {!isModal && (
          <Flex
            w="100%"
            px="32px"
            py="28px"
            direction="column"
            gap="18px"
          >
            <Box
              borderRadius={CONTENT_EXPERIENCE.panelRadius}
              border="1px solid"
              borderColor={CONTENT_EXPERIENCE.cardBorder}
              bg={CONTENT_EXPERIENCE.heroBg}
              boxShadow={CONTENT_EXPERIENCE.heroShadow}
              overflow="hidden"
            >
              <Flex px={{ base: 5, md: 8 }} py={{ base: 5, md: 7 }} direction="column" gap="18px">
                <Flex alignItems="center" justifyContent="space-between" gap="16px" flexWrap="wrap">
                  <HStack spacing="12px" align="center">
                    <Button variant="icon" onClick={handleGoBack}>
                      <Icon color="solid.700"><ArrowLeftIcon /></Icon>
                    </Button>
                    <Box>
                      <Text fontSize={{ base: '24px', md: '28px' }} fontWeight="700" color="solid.900">
                        {quizMode
                          ? contentType === ContentType.Training
                            ? 'Take training'
                            : isSurveyLike
                            ? 'Answer content'
                            : 'Review content'
                          : isSurveyLike
                          ? 'Build response flow'
                          : 'Build learning flow'}
                      </Text>
                      <Text mt="4px" fontSize="14px" color="solid.600" maxW="840px">
                        {contentDescription}
                      </Text>
                    </Box>
                  </HStack>

                  <NavigationTab
                    selectedTab={selectedNavTab}
                    onSelectTab={handleTabChange}
                    tabItems={tabItems}
                    questionnaire={true}
                  />
                </Flex>

                <HStack spacing="12px" flexWrap="wrap" align="stretch">
                  <Box
                    minW="180px"
                    px="16px"
                    py="14px"
                    borderRadius="18px"
                    bg="rgba(255,255,255,0.78)"
                    border="1px solid rgba(255,255,255,0.8)"
                  >
                    <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="solid.500">
                      Pages
                    </Text>
                    <Text mt="6px" fontSize="26px" fontWeight="700" color="solid.900">
                      {nonQuizPages.length || 1}
                    </Text>
                  </Box>
                  <Box
                    minW="180px"
                    px="16px"
                    py="14px"
                    borderRadius="18px"
                    bg="rgba(255,255,255,0.78)"
                    border="1px solid rgba(255,255,255,0.8)"
                  >
                    <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="solid.500">
                      Current Step
                    </Text>
                    <Text mt="6px" fontSize="26px" fontWeight="700" color="solid.900">
                      {currentLogicalStep}
                    </Text>
                  </Box>
                  {contentType === ContentType.Learning && (
                    <Box
                      minW="220px"
                      px="16px"
                      py="14px"
                      borderRadius="18px"
                      bg="rgba(255,255,255,0.78)"
                      border="1px solid rgba(255,255,255,0.8)"
                    >
                      <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="solid.500">
                        Learning Flow
                      </Text>
                    <Text mt="6px" fontSize="18px" fontWeight="700" color="solid.900">
                        {currentLearningUrlItem ? 'External link' : 'Content page'}
                      </Text>
                    </Box>
                  )}
                  {contentType === ContentType.Training && (
                    <Box
                      minW="180px"
                      px="16px"
                      py="14px"
                      borderRadius="18px"
                      bg="rgba(255,255,255,0.78)"
                      border="1px solid rgba(255,255,255,0.8)"
                    >
                      <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="solid.500">
                        Minimum Score
                      </Text>
                      <Text mt="6px" fontSize="26px" fontWeight="700" color="solid.900">
                        {minimumScore}%
                      </Text>
                    </Box>
                  )}
                </HStack>
              </Flex>
            </Box>
          </Flex>
        )}

        {/* ── Page tabs ─────────────────────────────────────────────── */}
        {!quizMode && (
          <Box w="100%" bg="white" flexShrink={0}>
            <HStack
              spacing="0px"
              align="center"
              px="24px"
              borderBottom="1px solid"
              borderColor="solid.100"
              h="44px"
            >
              {nonQuizPages.map((page: any, i: number) => {
                const fullIndex = pages.indexOf(page) + 1
                const isActive  = currentPage === fullIndex
                const isEditing = editingPageId === page.id
                const pageName  =
                  pageNames[page.id] !== undefined
                    ? pageNames[page.id]
                    : page?.name || `Page ${i + 1}`
                const canDelete = !userMode && nonQuizPages.length > 1

                return (
                  <HStack
                    key={page.id}
                    spacing="4px"
                    px="12px"
                    h="100%"
                    cursor="pointer"
                    _hover={{ bg: 'solid.25' }}
                    borderBottomWidth="2px"
                    borderBottomColor={isActive ? 'solid.900' : 'transparent'}
                    onClick={() => {
                      if (!isEditing) setCurrentPage(fullIndex)
                    }}
                  >
                    {isEditing ? (
                      <Input
                        size="sm"
                        value={pageName}
                        autoFocus
                        w="100px"
                        fontSize="13px"
                        h="28px"
                        borderColor="solid.300"
                        _focus={{ boxShadow: 'none', borderColor: 'solid.500' }}
                        onChange={(e) =>
                          setPageNames((prev) => ({ ...prev, [page.id]: e.target.value }))
                        }
                        onBlur={async () => {
                          const trimmed = String(pageName || '').trim()
                          if (
                            trimmed.toLowerCase() === 'quiz' &&
                            !isQuizPageName(page?.name)
                          ) {
                            handleToast(
                              toast,
                              false,
                              'Invalid name',
                              'Only the existing Quiz page can be named "Quiz".',
                            )
                            setPageNames((prev) => ({ ...prev, [page.id]: page.name }))
                            setEditingPageId(null)
                            return
                          }
                          setEditingPageId(null)
                          setPages((prev) =>
                            prev.map((pg) =>
                              pg.id === page.id ? { ...pg, name: trimmed } : pg,
                            ),
                          )
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                          if (e.key === 'Escape') {
                            setEditingPageId(null)
                            setPageNames((prev) => ({
                              ...prev,
                              [page.id]: page.name,
                            }))
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Text
                        fontSize="13px"
                        fontWeight={isActive ? '600' : '400'}
                        color={isActive ? 'solid.900' : 'solid.600'}
                        onDoubleClick={(e) => {
                          if (userMode) return
                          e.stopPropagation()
                          setEditingPageId(page.id)
                          setPageNames((prev) => ({
                            ...prev,
                            [page.id]: page.name || `Page ${i + 1}`,
                          }))
                        }}
                        userSelect="none"
                      >
                        {pageName}
                      </Text>
                    )}

                    {canDelete && !isEditing && (
                      <Box
                        as="button"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="16px"
                        h="16px"
                        borderRadius="full"
                        color="solid.400"
                        _hover={{ color: 'error.100', bg: 'error.0' }}
                        onClick={(e: any) => {
                          e.stopPropagation()
                          handleDeleteQuestionsForPage(page.id)
                          setPages((prev) => {
                            const filtered  = prev.filter((pg) => pg.id !== page.id)
                            const deletedIdx = prev.findIndex((pg) => pg.id === page.id)
                            const newIdx    = Math.max(0, deletedIdx - 1)
                            setCurrentPage(filtered.length ? newIdx + 1 : 1)
                            return filtered
                          })
                        }}
                        flexShrink={0}
                      >
                        <Icon boxSize="12px" fill="none"><Trash02Icon /></Icon>
                      </Box>
                    )}
                  </HStack>
                )
              })}

              {/* Add page button */}
              {!userMode && (
                <Box
                  as="button"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="28px"
                  h="28px"
                  ml="4px"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="solid.100"
                  color="solid.500"
                  bg="white"
                  flexShrink={0}
                  _hover={{ bg: 'solid.25', borderColor: 'solid.300', color: 'solid.900' }}
                  title="Add page"
                  onClick={async () => {
                    if (!contentId) return
                    try {
                      const { createPage, fetchPages: fp } = await import('../../../api/content/contentPage')
                      const nonQuiz = pages.filter((p: any) => !isQuizPageName(p?.name))
                      const maxIdx  = nonQuiz.reduce((m: number, p: any) => Math.max(m, p?.index || 0), 0)
                      const newPageIndex = maxIdx + 1
                      const created = await createPage(String(contentId), {
                        name: `Name Page ${newPageIndex}`,
                        index: newPageIndex,
                      })
                      const res: any = await fp(String(contentId))
                      const newPages: any[] = Array.isArray(res)
                        ? res
                        : Array.isArray(res?.data)
                        ? res.data
                        : []
                      const sorted = [...newPages].sort(
                        (a: any, b: any) => (a?.index || 0) - (b?.index || 0),
                      )
                      setPages(sorted)
                      const newPageIdx = sorted.findIndex((p: any) => p.index === newPageIndex)
                      setCurrentPage(newPageIdx !== -1 ? newPageIdx + 1 : sorted.length)
                      if (created?.id) handleAddDefaultQuestionForPage(created.id)
                    } catch {
                      handleToast(toast, false, 'Failed to add page', '')
                    }
                  }}
                >
                  <AddIcon boxSize="10px" />
                </Box>
              )}
            </HStack>

            {/* ── Toolbar ─────────────────────────────────────────────── */}
            {currentPageObj && !userMode && (
              <Flex
                align="center"
                gap="12px"
                px="24px"
                py="8px"
                borderBottom="1px solid"
                borderColor="solid.50"
                bg="solid.0"
                minH="44px"
              >
                {/* Page duration */}
                <Flex align="center" gap="6px" flexShrink={0}>
                  <Text fontSize="12px" fontWeight="500" color="solid.500" whiteSpace="nowrap">
                    Page duration
                  </Text>
                  <InputGroup size="sm" w="64px">
                    <Input
                      type="number"
                      h="26px"
                      fontSize="12px"
                      placeholder="0"
                      min="1"
                      step="1"
                      textAlign="center"
                      pr="18px"
                      value={pageDurations[currentPage] || ''}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === '') {
                          setPageDurations((prev) => ({ ...prev, [currentPage]: 0 }))
                          return
                        }
                        if (/^[1-9]\d*$/.test(v))
                          setPageDurations((prev) => ({ ...prev, [currentPage]: parseInt(v) }))
                      }}
                      onKeyDown={(e) => {
                        if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault()
                      }}
                      borderColor="solid.200"
                      _focus={{ borderColor: 'solid.500', boxShadow: 'none' }}
                    />
                    <InputRightElement
                      w="16px"
                      h="100%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      pr="2px"
                    >
                      <Button
                        size="xxs"
                        variant="hoverEffect"
                        p={0}
                        h="12px"
                        onClick={() => {
                          const next = (pageDurations[currentPage] || 0) + 1
                          setPageDurations((prev) => ({ ...prev, [currentPage]: next }))
                        }}
                      >
                        <Icon boxSize="10px" color="solid.400" fill="none"><ChevronUpIcon /></Icon>
                      </Button>
                      <Button
                        size="xxs"
                        variant="hoverEffect"
                        p={0}
                        h="12px"
                        onClick={() => {
                          const next = Math.max(1, (pageDurations[currentPage] || 1) - 1)
                          setPageDurations((prev) => ({ ...prev, [currentPage]: next }))
                        }}
                      >
                        <Icon boxSize="10px" color="solid.400" fill="none"><ChevronDownIcon /></Icon>
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <Text fontSize="12px" color="solid.400">min</Text>
                </Flex>

                <Divider />

                {/* Quiz duration */}
                <Flex align="center" gap="6px" flexShrink={0}>
                  <Text fontSize="12px" fontWeight="500" color="solid.500" whiteSpace="nowrap">
                    Quiz duration
                  </Text>
                  <InputGroup size="sm" w="64px">
                    <Input
                      type="number"
                      h="26px"
                      fontSize="12px"
                      placeholder="0"
                      min="1"
                      step="1"
                      textAlign="center"
                      pr="18px"
                      value={quizDurationValue as any}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === '') {
                          setPageDurations((prev) => ({ ...prev, [quizDurationKey]: 0 }))
                          return
                        }
                        if (/^[1-9]\d*$/.test(v))
                          setPageDurations((prev) => ({ ...prev, [quizDurationKey]: parseInt(v) }))
                      }}
                      onKeyDown={(e) => {
                        if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault()
                      }}
                      borderColor="solid.200"
                      _focus={{ borderColor: 'solid.500', boxShadow: 'none' }}
                    />
                    <InputRightElement
                      w="16px"
                      h="100%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      pr="2px"
                    >
                      <Button
                        size="xxs"
                        variant="hoverEffect"
                        p={0}
                        h="12px"
                        onClick={() => {
                          const cur = Number(quizDurationValue) || 0
                          setPageDurations((prev) => ({ ...prev, [quizDurationKey]: cur + 1 }))
                        }}
                      >
                        <Icon boxSize="10px" color="solid.400" fill="none"><ChevronUpIcon /></Icon>
                      </Button>
                      <Button
                        size="xxs"
                        variant="hoverEffect"
                        p={0}
                        h="12px"
                        onClick={() => {
                          const cur = Number(quizDurationValue) || 1
                          setPageDurations((prev) => ({
                            ...prev,
                            [quizDurationKey]: Math.max(1, cur - 1),
                          }))
                        }}
                      >
                        <Icon boxSize="10px" color="solid.400" fill="none"><ChevronDownIcon /></Icon>
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <Text fontSize="12px" color="solid.400">min</Text>
                </Flex>

                {/* Min score — training only */}
                {contentType === ContentType.Training && (
                  <>
                    <Divider />
                    <Flex align="center" gap="6px" flexShrink={0}>
                      <Text fontSize="12px" fontWeight="500" color="solid.500" whiteSpace="nowrap">
                        Min score
                      </Text>
                      <InputGroup size="sm" w="64px">
                        <Input
                          type="number"
                          h="26px"
                          fontSize="12px"
                          value={minimumScore}
                          min={0}
                          max={100}
                          textAlign="center"
                          pr="18px"
                          borderColor="solid.200"
                          _focus={{ borderColor: 'solid.500', boxShadow: 'none' }}
                          onChange={(e) => {
                            const v       = Number(e.target.value)
                            const clamped = Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v))
                            setMinimumScore(clamped)
                          }}
                        />
                        <InputRightElement
                          w="16px"
                          h="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          pr="2px"
                        >
                          <Button size="xxs" variant="hoverEffect" p={0} h="12px" onClick={increment}>
                            <Icon boxSize="10px" color="solid.400" fill="none"><ChevronUpIcon /></Icon>
                          </Button>
                          <Button size="xxs" variant="hoverEffect" p={0} h="12px" onClick={decrement}>
                            <Icon boxSize="10px" color="solid.400" fill="none"><ChevronDownIcon /></Icon>
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                      <Text fontSize="12px" color="solid.400">%</Text>
                    </Flex>
                  </>
                )}

                {/* Right-side action buttons */}
                <Flex ml="auto" gap="8px" align="center" flexShrink={0}>
                  <Button
                    variant="white"
                    size="sm"
                    isLoading={isActionInProgress}
                    onClick={() => handleUpdateContent(true)}
                  >
                    Save Draft
                  </Button>
                  <Button
                    variant="white"
                    size="sm"
                    isDisabled={unsavedChanges}
                    onClick={() => {
                      const firstNonTestIdx = pages.findIndex((pg: any) => !isQuizPageName(pg?.name))
                      setQuizMode(true)
                      setCurrentPage(firstNonTestIdx >= 0 ? firstNonTestIdx + 1 : 1)
                    }}
                  >
                    Preview Learner Flow
                  </Button>
                  <Button
                    variant="mono"
                    size="sm"
                    isLoading={isActionInProgress}
                    onClick={handlePublish}
                  >
                    Publish
                  </Button>
                </Flex>
              </Flex>
            )}
          </Box>
        )}

        {/* ── Main body ─────────────────────────────────────────────── */}
        <Flex
          w="100%"
          direction={{ base: 'column', lg: 'row' }}
          flex="1"
          bg="solid.0"
          minH={0}
        >
          {/* Left sidebar (add-question buttons) */}
          {!quizMode && !userMode && (
            <Box width="64px" flexShrink={0} zIndex={2} pt={3}>
              <SideBar
                handleAddQuestion={handleAddQuestion}
                bigger={true}
                contentType={contentType}
              />
            </Box>
          )}

          {/* Centre: cards */}
          <Flex
            flex="1"
            direction="column"
            overflowY="auto"
            px="24px"
            py="24px"
            gap="16px"
            minW={0}
            borderRadius={CONTENT_EXPERIENCE.panelRadius}
            bg={CONTENT_EXPERIENCE.glassPanelBg}
            backdropFilter="blur(14px)"
            border="1px solid"
            borderColor={CONTENT_EXPERIENCE.glassBorder}
            boxShadow={CONTENT_EXPERIENCE.glassShadow}
            m="12px"
          >

            {/* Floating Title card */}
            <Box
              bg={CARD_PALETTES[0].bg}
              borderRadius="12px"
              p="20px"
              transition="box-shadow 0.15s ease"
              boxShadow={titleFocused ? SHADOW_ACTIVE : SHADOW_DEFAULT}
              onFocusCapture={() => setTitleFocused(true)}
              onBlurCapture={() => setTitleFocused(false)}
            >
              <Text fontSize="13px" fontWeight="600" mb="8px" color="solid.700">
                Title
              </Text>
              <Input
                variant="content"
                value={contentTitle}
                placeholder="Enter title"
                readOnly
                fontSize="18px"
                fontWeight="600"
                h="48px"
                border="none"
                bg="transparent"
                style={{ background: 'transparent' }}
                _focus={{ boxShadow: 'none', bg: 'transparent' }}
              />
            </Box>

            {/* Quiz mode controls (top) */}
            {quizMode && (
              <Box
              borderRadius={CONTENT_EXPERIENCE.cardRadius}
              border="1px solid"
              borderColor={isCurrentQuizPage ? 'solid.900' : 'solid.50'}
              bg={CONTENT_EXPERIENCE.infoPanelBg}
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
              >
                <Flex justify="space-between" align="flex-start" gap="16px" flexWrap="wrap">
                  <Box maxW="720px">
                    <Text
                      fontSize="12px"
                      fontWeight="700"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color={isCurrentQuizPage ? 'solid.900' : 'solid.500'}
                    >
                      {isCurrentQuizPage ? 'Content page' : 'Learning page'}
                    </Text>
                    <Text mt="6px" fontSize="22px" fontWeight="700" color="solid.900">
                      {visiblePageTitle}
                    </Text>
                    <Text mt="6px" fontSize="14px" color="solid.600">
                      {visiblePageSubtitle}
                    </Text>
                  </Box>

                  <Flex justify="flex-end" gap="8px" flexWrap="wrap">
                    {!quizSubmitted ? (
                      <>
                        <Button variant="white" size="sm" onClick={() => (onClose ? onClose() : handleExitQuiz())}>
                          Cancel Attempt
                        </Button>
                        {isCurrentQuizPage && (
                          <Button variant="mono" size="sm" onClick={handleQuizSubmit}>
                            Submit Quiz
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button variant="white" size="sm" onClick={handleExitQuiz}>
                        {userMode ? 'Return to Content Overview' : 'Return to Adding E-Learning Content'}
                      </Button>
                    )}
                  </Flex>
                </Flex>

                <HStack spacing="10px" mt="16px" flexWrap="wrap">
                  {logicalPages.map((page: any, idx: number) => {
                    const pageIndex = pages.findIndex((candidate: any) => candidate?.id === page?.id)
                    const isActive = currentPage === pageIndex + 1
                    const label = page?.name || `Page ${idx + 1}`

                    return (
                      <Button
                        key={page?.id ?? `${label}-${idx}`}
                        size="sm"
                        variant={isActive ? 'mono' : 'white'}
                        onClick={() => setCurrentPage(pageIndex + 1)}
                        borderRadius="8px"
                        minW="unset"
                        px="14px"
                      >
                        {label}
                      </Button>
                    )
                  })}
                </HStack>
              </Box>
            )}

            {quizMode && contentType === ContentType.Learning && !isCurrentQuizPage && (
              <Box
                borderRadius={CONTENT_EXPERIENCE.cardRadius}
                border="1px solid"
                borderColor="solid.50"
                bg={CONTENT_EXPERIENCE.infoPanelBg}
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
              >
                <Flex justify="space-between" align="flex-start" gap="16px" flexWrap="wrap">
                  <Box maxW="760px">
                    <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="solid.500">
                      Learning step
                    </Text>
                    <Text mt="6px" fontSize="20px" fontWeight="700" color="solid.900">
                      {currentLearningUrlItem ? 'Open the learning material' : 'Review this page'}
                    </Text>
                    <Text mt="6px" fontSize="14px" color="solid.600">
                      {currentLearningUrlItem
                        ? 'Use the external training link, come back here, then confirm completion and share any feedback before moving on.'
                        : 'Review the learning material on this page, then confirm completion and continue through the flow.'}
                    </Text>
                  </Box>

                  <Flex gap="8px" flexWrap="wrap">
                    {currentLearningUrlItem?.url && (
                      <Button
                        as="a"
                        href={/^https?:\/\//i.test(currentLearningUrlItem.url) ? currentLearningUrlItem.url : `https://${currentLearningUrlItem.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="mono"
                        size="sm"
                      >
                        Launch Learning
                      </Button>
                    )}
                    {currentLearningConfirmation && (
                      <Button variant="white" size="sm" isDisabled>
                        Completion step below
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Box>
            )}

            {/* Question cards */}
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="newQuestions" isDropDisabled={quizMode}>
                {(provided: any) => (
                  <VStack
                    spacing="16px"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    align="stretch"
                    pb={16}
                  >
                    {Array.isArray(newQuestions) && newQuestions.length === 0 ? (
                      <Flex
                        align="center"
                        justify="center"
                        w="100%"
                        py="48px"
                      >
                        <Text color="solid.200" textStyle="l-md">
                          {quizMode
                            ? 'No questions available'
                            : 'Add content from the sidebar to get started...'}
                        </Text>
                      </Flex>
                    ) : (
                      Array.isArray(newQuestions) &&
                      currentPageQuestions.map((q) => {
                        const actualIndex = newQuestions.findIndex((nq) => nq.id === q.id)
                        const displayIndex = newQuestions
                          .slice(0, actualIndex)
                          .filter((question) =>
                            [
                              'short',
                              'paragraph',
                              'survey_multiple_choice',
                              'survey_rating',
                              'training_multiple_choice',
                              'training_checkbox',
                              'checkbox',
                              'survey_text',
                            ].includes(question.type),
                          ).length
                        const palette = CARD_PALETTES[actualIndex % CARD_PALETTES.length]
                        const isSectionItem = q.type === ContentItemType.Section

                        return (
                          <Draggable
                            key={q.id}
                            draggableId={String(q.id)}
                            index={actualIndex}
                            isDragDisabled={quizMode}
                          >
                            {(provided: any) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{ width: '100%', ...provided.draggableProps.style }}
                                borderRadius={isSectionItem ? '0' : '12px'}
                                overflow={isSectionItem ? 'visible' : 'hidden'}
                                bg={isSectionItem ? 'transparent' : palette.bg}
                                transition={isSectionItem ? 'none' : 'box-shadow 0.15s ease'}
                                boxShadow={isSectionItem ? 'none' : activeCardId === q.id ? SHADOW_ACTIVE : SHADOW_DEFAULT}
                                onFocusCapture={isSectionItem ? undefined : () => setActiveCardId(q.id)}
                                onBlurCapture={
                                  isSectionItem ? undefined : () => setActiveCardId((prev) => (prev === q.id ? null : prev))
                                }
                              >
                                <MasterCard
                                  quizMode={quizMode}
                                  quizSubmitted={quizSubmitted}
                                  id={q.id}
                                  index={
                                    [
                                      'short',
                                      'paragraph',
                                      'survey_multiple_choice',
                                      'survey_rating',
                                      'training_multiple_choice',
                                      'training_checkbox',
                                      'checkbox',
                                      'survey_text',
                                    ].includes(q.type)
                                      ? displayIndex
                                      : 0
                                  }
                                  type={q.type}
                                  text={
                                    q.type === ContentItemType.LearningCheckbox
                                      ? q.title || q.label || ''
                                      : q.url || q.question || q.title || q.content || ''
                                  }
                                  label={q.label || q.short || ''}
                                  points={q.points || 0}
                                  media={q.media || ''}
                                  paragraph={q.paragraph || ''}
                                  short={q.short || ''}
                                  mcqAnswers={q.answers || []}
                                  contentType={contentType || ContentType.Skills}
                                  handleDelete={userMode ? () => {} : () => handleDeleteQuestion(q.id)}
                                  handleAddQuestion={handleAddQuestion}
                                  handleAddSection={() => {}}
                                  moveCard={moveQuestion}
                                  correct_feedback={q.correct_feedback}
                                  incorrect_feedback={q.incorrect_feedback}
                                  handleUpdate={
                                    userMode
                                      ? () => {}
                                      : (updatedFields) => handleUpdate(actualIndex, updatedFields)
                                  }
                                  onMCQSelect={
                                    q.type === 'survey_multiple_choice'
                                      ? handleSurveyMCQSelect
                                      : handleMCQSelect
                                  }
                                  onCheckboxToggle={handleCheckboxToggle}
                                  onRatingSelect={handleSurveyRatingSelect}
                                  onAnswer={handleSurveyTextAnswer}
                                  mcqSelectedIndex={
                                    q.type === ContentItemType.SurveyMCQ
                                      ? (surveyMCQSelections[String(q.id)] ?? q._userSelection ?? null)
                                      : (mcqSelections[String(q.id)] ?? q._userSelection ?? null)
                                  }
                                  checkboxSelectedIndexes={
                                    checkboxSelections[String(q.id)] ?? q._userCheckboxSelection ?? []
                                  }
                                  ratingValue={
                                    surveyRatingSelections[String(q.id)] ?? q._userRating ?? null
                                  }
                                  hasUser={true}
                                  enableEditor={q.type === ContentItemType.Paragraph && !userMode}
                                  dragHandleProps={!quizMode && !userMode ? provided.dragHandleProps : undefined}
                                />

                                {questionErrors[q.id] && (
                                  <Flex
                                    align="center"
                                    gap="8px"
                                    bg="error.10"
                                    color="error.100"
                                    px="16px"
                                    py="12px"
                                    fontSize="13px"
                                  >
                                    <Icon w="16px" h="16px" flexShrink={0}><WarningIcon /></Icon>
                                    <Text>{questionErrors[q.id]}</Text>
                                  </Flex>
                                )}
                              </Box>
                            )}
                          </Draggable>
                        )
                      })
                    )}
                    {provided.placeholder}
                  </VStack>
                )}
              </Droppable>
            </DragDropContext>

            {/* Modal quiz pagination */}
            {(isModal || userMode) &&
              quizMode &&
              !quizSubmitted &&
              (contentType === ContentType.Training || contentType === ContentType.Survey) && (
                <Flex
                  position="sticky"
                  w="100%"
                  bg="white"
                  zIndex={10}
                  justify="flex-end"
                  align="center"
                  gap="8px"
                  h="56px"
                  minH="56px"
                  py={0}
                  mt="20px"
                  flexWrap="nowrap"
                  borderTopWidth="1px"
                  borderColor="solid.50"
                >
                  <Button variant="white" onClick={() => (onClose ? onClose() : handleExitQuiz())}>
                    Cancel Attempt
                  </Button>
                  <Button
                    variant="mono"
                    onClick={handlePrevPage}
                    isDisabled={
                      logicalOrder.length === 0 || logicalPosition <= 0
                    }
                  >
                    Previous Page
                  </Button>
                  {isLastLogicalPage() ? (
                    <Button variant="mono" onClick={handleQuizSubmit}>
                      Submit Quiz
                    </Button>
                  ) : (
                    <Button variant="mono" onClick={handleNextPage}>
                      {isCurrentQuizPage ? 'Finish' : 'Next Page'}
                    </Button>
                  )}
                </Flex>
              )}
          </Flex>

          {/* Right sidebar (survey-like content only) */}
          {/* {!isSurveyLike && (
            <VStack
              borderLeft="1px solid"
              borderColor="solid.100"
              bg="white"
              maxW="290px"
              w="100%"
            >
              <QuestionnaireSidebar
                onPublish={handlePublish}
                isDraftSave={isActionInProgress}
                isSubmitSave={isActionInProgress}
                draftChanged={unsavedChanges}
                hasContentId={!!contentId}
                contentId={contentId}
                pages={pages}
                setPages={setPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                editingPageId={editingPageId}
                setEditingPageId={setEditingPageId}
                pageNames={pageNames}
                setPageNames={setPageNames}
                pageDurations={pageDurations}
                setPageDurations={setPageDurations}
                quizMode={quizMode}
                setQuizMode={setQuizMode}
                setCurrentPageId={() => {}}
                handleAddDefaultQuestionForPage={handleAddDefaultQuestionForPage}
                setNewQuestions={setNewQuestions}
                onDeleteQuestionsForPage={handleDeleteQuestionsForPage}
                contentType={contentType}
                isModal={isModal}
                userMode={userMode}
                unsavedChanges={unsavedChanges}
                minimumScore={minimumScore}
                onMinimumScoreChange={(v) => setMinimumScore(v)}
                onMinimumScoreInc={increment}
                onMinimumScoreDec={decrement}
              />
            </VStack>
          )} */}
        </Flex>
      </Flex>
    </Flex>
  )
}
